import React, { useState, useEffect, useRef, useCallback } from "react";
import DIframe from "../components/DIframe";
import "./NVPage.css";
import Header from "../components/Header";
import Card from "../components/Card";
import Headline from "../components/Headline";
import LoadingSpinner from "../components/LoadingSpinnerBox";
import ResultBox from "../components/ResultBox";
import PerformRequestBox from "../components/PerformRequestBox";
import Button from "../components/Button";
import { performPiRequest, generateAuthUrl, verifyRecaptchaToken } from "../utils/NetworkUtils";
import ReportIssueBtn from "../components/ReportIssueBtn";
import ReportIssuePopup from "../components/ReportIssuePopup";
import Footer from "../components/Footer";
import globalConfig from "../utils/config.json";
import TacPopup from "../components/TacPopup";
import CountrySelectorInputField from "../components/CountrySelectorInputField";
import ReCAPTCHA from "react-google-recaptcha";
import AdditionalProductsBox from "../components/AdditionalProductsBox";

const callStatus = { callStarted: false };

function NVPage() {
  const connectionTimerRef = useRef(null);
  const verifiedTimerRef = useRef(null);
  const [iFrameLoading, setIframeLoading] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [verifyBtnDisabled, setVerifyBtnDisabled] = useState(true);
  const [showPerformRequest, setShowPerformRequest] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [result, setResult] = useState(null); //success (number successfully verified), failure (number not verified), warn (any problem occurred or timeout passed), null
  const [MSISDN, setMSISDN] = useState(sessionStorage.getItem("MSISDN") || "");
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [showTac, setShowTac] = useState(false);
  const [selectedCountryConfig, setSelectedCountryConfig] = useState(getCountryConfigFromCc(sessionStorage.getItem("CC")) || globalConfig.clients[0]);
  const [authUrl, setAuthUrl] = useState("");
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [additionalProducts, setAdditionalProducts] = useState(null);
  const reCaptchaRef = useRef(null);

  const setResultByUrlParams = useCallback((urlParams, msisdn, countryConfig) => {
    console.log("setResultByUrlParams MSISDN: [" + msisdn + "] urlParams: " + urlParams);
    if (urlParams.has("code")) {
      if (callStatus.callStarted) {
        console.log("Call already started, ignoring");
      } else {
        console.log("Valid code returned: starting PI request");
        callStatus.callStarted = true;
        performPiRequest(premiumInfoRequestCallBack, msisdn, urlParams.get("code"), countryConfig);
      }
    } else {
      console.log("Unexpected urlParams: " + urlParams);
      setResult("warn");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const url = window.location.href;

    //msisdn and countryConfig are taken from the sessionStorage (the same we put in the setState initialization), to avoid the useEffect to be called again when the state is updated
    const msisdn = sessionStorage.getItem("MSISDN") || "";
    const countryConfig = getCountryConfigFromCc(sessionStorage.getItem("CC")) || globalConfig.clients[0];

    console.log("NVPage useEffect. Saved MSISDN: [" + msisdn + "] selectedCountryConfig: [" + (countryConfig ? countryConfig.cc : null) + "] Url: " + url);

    const urlParams = new URLSearchParams(new URL(url).search);
    if (urlParams.has("code") || urlParams.has("error_description")) {
      setResultByUrlParams(urlParams, msisdn, countryConfig);
      setInputDisabled(true);
      setShowPerformRequest(false);
    }

    return clearConnectionTimeout();
  }, [setResultByUrlParams]);

  useEffect(() => {
    //A regex to validate a mobile phone number without the international prefix
    const regex = /^[0-9]{9,15}$/;
    // const regex = /^(\+|00)?[0-9]{11,15}$/; //A regex to validate a mobile phne number with or without the international prefix

    if (MSISDN.match(regex)) {
      console.log("valid number");
      setVerifyBtnDisabled(false);
    } else {
      console.log("invalid: number[" + MSISDN + "]");
      setVerifyBtnDisabled(true);
    }
  }, [MSISDN]);

  /**
   *
   * @param {string} url the redirect url returned by the iFrame with the code to perform the PremiumInfo request, or the MSISDN could not be determined error
   * @param {string} error the error returned by the iFrame or "Loading timed out" in case of passed timeout
   */
  function handleFinishedLoading(url, error) {
    console.log("handleFinishedLoading url:" + url + " error:" + error);

    if (url) {
      setResultByUrlParams(new URLSearchParams(new URL(url).search), MSISDN, selectedCountryConfig);
    }
    if (error) {
      setResult("warn");
    }

    var millisecondsToWait = 10;
    setTimeout(function () {
      resetStatus(); //If I don't add this timeout, the images are not loaded correctly
    }, millisecondsToWait);
  }

  function resetStatus() {
    setShowLoading(false);
    clearConnectionTimeout();
    setIframeLoading(false);
  }

  function onConnectionTimeout() {
    handleFinishedLoading(null, "Loading timed out");
  }

  function onVerifiedTimeout() {
    console.log("onVerifiedTimeout");
    setResult("expired");
  }

  function onInputChanged(value) {
    setMSISDN(value);
  }

  function getCountryConfigFromCc(cc) {
    return globalConfig.clients.find((client) => client.cc === cc);
  }

  // -------------- CALLBACKS --------------
  function iFrameCallBack(url, error) {
    handleFinishedLoading(url, error);
  }

  function premiumInfoRequestCallBack(json) {
    console.log("premiumInfoRequestCallBack response: " + JSON.stringify(json, null, 2));
    try {
      if (json.premiuminfo.response.payload.device_msisdn_verified === true) {
        setAdditionalProducts(json.additionalProducts);
        setResult("success");
        verifiedTimerRef.current = setTimeout(onVerifiedTimeout, globalConfig.verified_timeout);
      } else {
        setResult("failure");
      }
    } catch (error) {
      setResult("warn");
    }
  }

  function reportIssueCallback(result, error) {
    if (result) {
      console.log("Report issue success: " + JSON.stringify(result));
      setShowReportIssue(false);
    }
    if (error) {
      console.log("Report issue error: " + error);
      alert("An error occurred sending the feedback. Please try again later.");
    }
  }

  function onCountrySelectedCallback(countryConfig) {
    console.log("onCountrySelectedCallback: " + countryConfig.cc);
    setSelectedCountryConfig(countryConfig);
    sessionStorage.setItem("CC", countryConfig.cc);
  }

  function clearVerifiedTimeout() {
    console.log("clearing verifiedTimerRef timeout");
    clearTimeout(verifiedTimerRef.current);
  }

  function verifyRecaptchaTokenCallback(json) {
    console.log("verifyRecaptchaTokenCallback response: " + JSON.stringify(json, null, 2));

    if (json &&json.success) {
      onRecaptchaVerified();
    } else {
      console.log("Recaptcha verification failed");
      setResult("warn");
      resetStatus();
    }
  }

  // -------------- Click listeners --------------
  /**
   * This function is called when the user clicks on the "Verify" button.
   * When the token is ready, the onRecaptchaChange function is called.
   */
  function onVerifyClick() {
    setShowPerformRequest(false);
    setShowLoading(true);
    startConnectionTimeout();
    setInputDisabled(true);
    setResult(null);
    sessionStorage.setItem("MSISDN", MSISDN);
    reCaptchaRef.current.execute();
  }

  /**
   * The token is passed to this function when the user has successfully completed the reCaptcha challenge.
   * @param {string} token
   */
  function onRecaptchaChange(token) {
    console.log("onRecaptchaChange");
    if (!recaptchaVerified) {
      //Added because onChange is called multiple times on reCaptcha
      setRecaptchaVerified(true);
      verifyRecaptchaToken(token, verifyRecaptchaTokenCallback);
    }
  }

  function startConnectionTimeout() {
    connectionTimerRef.current = setTimeout(onConnectionTimeout, globalConfig.connection_timeout);
  }

  function clearConnectionTimeout() {
    clearTimeout(connectionTimerRef.current);
  } 

  function onRecaptchaVerified() {
    try {
      const authUrl = generateAuthUrl(selectedCountryConfig);
      setAuthUrl(authUrl);

      const urlParams = new URLSearchParams(new URL(window.location.href).search);
      if (urlParams.has("overrideEhProtocol")) {
        //This is used for testing purposes only
        console.log("Overridden EH protocol. Forcing indirect flow");
        startIndirectFlow(authUrl);
      } else {
        switch (selectedCountryConfig.heProtocol) {
          case "https":
            startDirectFlow();
            break;
          case "http":
          default: //MSISDN is saved in sessionStorage to be used on page refresh for the Indirect flow, to perform the PremiumInfo call
            startIndirectFlow(authUrl);
            break;
        }
      }
    } catch (error) {
      console.log("Error: " + error);
      setResult("warn");
      setShowLoading(false);
    }
  }

  function startDirectFlow() {
    console.log("starting Ifrme flow");
    setIframeLoading(true);
  }

  function startIndirectFlow(authUrl) {
    console.log("starting INDIRECT flow");
    window.location.href = authUrl;
  }

  function onTacClick() {
    setShowTac(true);
  }

  function onNewVerificationClick() {
    // setShowPerformRequest(true);
    // setResult(null);
    // setInputDisabled(false);
    // sessionStorage.removeItem("MSISDN");
    window.location.href = process.env.REACT_APP_REDIRECT_URI;
  }

  function onReportAnIssueBtnClick() {
    setShowReportIssue(true);
  }

  return (
    <div className="externalContiner">
      <Header />
      <div className="mainPage">
        <Headline />
        <div className="cards">
          <Card>
            <CountrySelectorInputField
              id="MSISDN-input-field"
              placeholder="Mobile number"
              hint="The number to be verified"
              disabled={inputDisabled}
              onChange={(event) => onInputChanged(event.target.value)}
              value={MSISDN}
              onCountrySelected={onCountrySelectedCallback}
              selectedCountryConfig={selectedCountryConfig}
            />

            <div className="dynamicContent">
              {showPerformRequest ? <PerformRequestBox onTacClick={onTacClick} onVerifyClick={onVerifyClick} btnDisabled={verifyBtnDisabled | inputDisabled} /> : ""}
              {showLoading ? <LoadingSpinner /> : ""}
              {result ? <ResultBox result={result} MSISDN={MSISDN} selectedCountryConfig={selectedCountryConfig} /> : ""}
              {result === "success" && additionalProducts ? (
                <AdditionalProductsBox MSISDN={MSISDN} selectedCountryConfig={selectedCountryConfig} additionalProducts={additionalProducts} clearVerifiedTimeout={() => clearVerifiedTimeout()} showError={() => setResult("warn")}
                startConnectionTimeout={() => startConnectionTimeout()} 
                clearConnectionTimeout={() => clearConnectionTimeout()}/>
              ) : (
                ""
              )}
            </div>
          </Card>
          {result ? <Button onClick={onNewVerificationClick} text="New verification" /> : ""}
          <ReportIssueBtn onClick={onReportAnIssueBtnClick} />
          {/* <textarea disabled id="console" className="console" value={consoleLog} /> */}
          <ReCAPTCHA
            style={{ display: "inline-block" }}
            theme="dark"
            size="invisible"
            ref={reCaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={onRecaptchaChange}
            // asyncScriptOnLoad={asyncScriptOnLoad}
          />
        </div>
        {showReportIssue ? <ReportIssuePopup onCancelClick={() => setShowReportIssue(false)} callBack={reportIssueCallback} /> : ""}
        {showTac ? <TacPopup onCancelClick={() => setShowTac(false)} /> : ""}
      </div>
      <Footer />
      {iFrameLoading ? <DIframe callBack={iFrameCallBack} authUrl={authUrl} MSISDN={MSISDN} /> : ""}
    </div>
  );
}

export default NVPage;
